import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { WorkKanban } from '../Kanban/WorkKanban';
import { WorkCalendar } from '../Calendar/WorkCalendar';
import AnalyticsPanel from '../Analytics/AnalyticsPanel';
import { useWorkObjects } from '../../hooks/useWorkObjects';
import { Plus, BarChart3, Calendar, Kanban } from 'lucide-react';

export const WorkObjectsPanel: React.FC = () => {
  const [fallbackMode, setFallbackMode] = useState(false);
  const [workObjectsData, setWorkObjectsData] = useState({ items: [], loading: false });

  let hookData, hookLoading, hookCreateWorkObject;
  try {
    const hookResult = useWorkObjects();
    hookData = hookResult.data;
    hookLoading = hookResult.loading;
    hookCreateWorkObject = hookResult.createWorkObject;
  } catch (error) {
    console.warn('Work objects API unavailable, using fallback mode:', error);
    setFallbackMode(true);
  }

  const data = fallbackMode ? workObjectsData : (hookData || { items: [] });
  const loading = fallbackMode ? workObjectsData.loading : (hookLoading || false);

  const handleCreateWorkObject = async () => {
    try {
      if (fallbackMode) {
        const newItem = {
          id: `fallback-${Date.now()}`,
          title: 'New Work Item',
          status: 'Backlog',
          assignees: [],
          description: 'Created from unified WorkObjects panel (Demo Mode)'
        };
        setWorkObjectsData(prev => ({
          ...prev,
          items: [...prev.items, newItem]
        }));
        console.log('Created work object in fallback mode:', newItem);
      } else {
        await hookCreateWorkObject({
          title: 'New Work Item',
          status: 'Backlog',
          assignees: [],
          description: 'Created from unified WorkObjects panel'
        });
      }
    } catch (error) {
      console.error('Failed to create work object:', error);
      setFallbackMode(true);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="h-full bg-gradient-to-br from-white via-gray-50 to-white"
      style={{ fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif" }}
    >
      <div className="p-6">
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-4xl font-bold bg-gradient-to-r from-[#4267A1] via-[#5A7BC4] to-[#4267A1] bg-clip-text text-transparent mb-2">
              Unified Work Objects
            </h1>
            <p className="text-gray-600 text-lg font-medium">Manage your work across multiple views</p>
          </div>
          <button
            onClick={handleCreateWorkObject}
            className="bg-gradient-to-r from-[#4267A1] to-[#2E4F7A] hover:from-[#2E4F7A] hover:to-[#1e3a5f] text-white px-6 py-3 rounded-xl transition-all duration-300 flex items-center gap-2 font-medium shadow-lg shadow-[#4267A1]/20 hover:shadow-[#4267A1]/30 hover:-translate-y-0.5"
            style={{
              boxShadow: '0 4px 16px var(--cb-shadow-purple-glow), 0 0 0 1px rgba(255, 255, 255, 0.05) inset'
            }}
          >
            <Plus className="w-5 h-5" />
            Create Work Item
          </button>
        </div>

        <div className="mb-6 p-4 bg-gradient-to-r from-gray-50 to-white rounded-xl border border-gray-200 shadow-sm">
          <div className="flex items-center justify-between text-gray-700">
            <div className="flex items-center gap-6">
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 bg-gradient-to-r from-[#8A9150] to-[#9BA55F] rounded-full"></div>
                <span className="font-medium text-gray-700">Total Items: {data.items?.length || 0}</span>
              </div>
              <div className="flex items-center gap-2">
                <div className={`w-2 h-2 rounded-full ${loading ? 'bg-gradient-to-r from-yellow-400 to-yellow-500 animate-pulse' : fallbackMode ? 'bg-gradient-to-r from-orange-400 to-orange-500' : 'bg-gradient-to-r from-[#8A9150] to-[#9BA55F]'}`}></div>
                <span className="font-medium text-gray-700">Status: {loading ? 'Loading...' : fallbackMode ? 'Demo Mode' : 'Ready'}</span>
              </div>
            </div>
          </div>
          {fallbackMode && (
            <div className="mt-3 p-3 bg-orange-50 border border-orange-200 rounded-lg">
              <p className="text-orange-700 text-sm">
                ⚠️ Work Objects API unavailable. Running in demo mode with local data.
              </p>
            </div>
          )}
        </div>

        <Tabs defaultValue="kanban" className="h-full">
          <TabsList className="grid w-full grid-cols-3 bg-gradient-to-r from-gray-100 to-gray-50 border border-gray-200 rounded-xl p-1 shadow-sm">
            <TabsTrigger 
              value="kanban" 
              className="text-gray-600 data-[state=active]:bg-gradient-to-r data-[state=active]:from-[#4267A1] data-[state=active]:to-[#5A7BC4] data-[state=active]:text-white rounded-lg transition-all duration-300 flex items-center gap-2 font-medium hover:text-[#4267A1]"
            >
              <Kanban className="w-4 h-4" />
              Kanban Board
            </TabsTrigger>
            <TabsTrigger 
              value="calendar" 
              className="text-gray-600 data-[state=active]:bg-gradient-to-r data-[state=active]:from-[#4267A1] data-[state=active]:to-[#5A7BC4] data-[state=active]:text-white rounded-lg transition-all duration-300 flex items-center gap-2 font-medium hover:text-[#4267A1]"
            >
              <Calendar className="w-4 h-4" />
              Calendar View
            </TabsTrigger>
            <TabsTrigger 
              value="analytics" 
              className="text-gray-600 data-[state=active]:bg-gradient-to-r data-[state=active]:from-[#8A9150] data-[state=active]:to-[#9BA55F] data-[state=active]:text-white rounded-lg transition-all duration-300 flex items-center gap-2 font-medium hover:text-[#8A9150]"
            >
              <BarChart3 className="w-4 h-4" />
              Analytics Dashboard
            </TabsTrigger>
          </TabsList>
          
          <TabsContent value="kanban" className="mt-6 h-full">
            <WorkKanban />
          </TabsContent>
          
          <TabsContent value="calendar" className="mt-6 h-full">
            <WorkCalendar />
          </TabsContent>
          
          <TabsContent value="analytics" className="mt-6 h-full">
            <AnalyticsPanel />
          </TabsContent>
        </Tabs>
      </div>
    </motion.div>
  );
};

export default WorkObjectsPanel;
